import { useCallback } from 'react';

import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { SparkleIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Sparkle';
import { TitleMedium1 } from '@zola/zola-ui/src/typography/Headings';

import { useModal } from '~/contexts/ModalContext';

import styles from './vendorNewExperienceModal.module.less';

interface VendorNewExperienceModalProps {
  /**
   * Text for the primary button
   */
  startButtonText: string;

  /**
   * Hide the secondary button
   */
  hideSkipButton?: boolean;

  /**
   * Optional callback for when the primary button is clicked
   */
  onTourStart?: () => void;

  /**
   * Optional callback for when the secondary button is clicked
   */
  onTourSkip?: () => void;
}

export const useVendorNewExperienceModal = () => {
  const { showCustomModal } = useModal();

  const showVendorNewExperienceModal = useCallback(
    ({
      startButtonText,
      hideSkipButton,
      onTourStart,
      onTourSkip,
    }: VendorNewExperienceModalProps) => {
      showCustomModal(
        VendorNewExperienceModal,
        {
          startButtonText,
          hideSkipButton,
          onTourStart,
          onTourSkip,
        },
        {
          className: styles.vendorNewExperienceModalContainer,
          closeOnEsc: false,
          closeOnOverlayClick: false,
          mobileHalfSheet: true,
        }
      );
    },
    [showCustomModal]
  );

  return { showVendorNewExperienceModal };
};

export const VendorNewExperienceModal = ({
  startButtonText,
  hideSkipButton = false,
  onTourStart,
  onTourSkip,
}: VendorNewExperienceModalProps) => {
  const { hideModal } = useModal();

  const handleTourStart = () => {
    hideModal();
    onTourStart?.();
  };

  const handleTourSkip = () => {
    hideModal();
    onTourSkip?.();
  };

  return (
    <div className={styles.vendorNewExperienceModal}>
      <header>
        <SparkleIcon width={48} height={48} />
        <TitleMedium1 presentation="h4">Your new Zola experience is here!</TitleMedium1>
      </header>
      <ul className={styles.list}>
        <li>
          An updated <b>dashboard</b> to manage your listing’s performance
        </li>
        <li>
          A streamlined <b>leads</b> inbox to see your inquiries and discover prospects
        </li>
        <li>
          Access advice, tips and trends in the new <b>resources hub</b>
        </li>
      </ul>
      <footer>
        <ButtonV3 className={styles.button} onClick={handleTourStart} size="large">
          {startButtonText}
        </ButtonV3>
        {!hideSkipButton && (
          <LinkV2 className={styles.button} role="button" onClick={handleTourSkip} noTextTransform>
            Not right now
          </LinkV2>
        )}
      </footer>
    </div>
  );
};
